import { Link } from "react-router-dom"
import { motion } from "framer-motion"
import {
  ArrowRight,
  BarChart3,
  Clock,
  Globe,
  Shield,
  Sparkles,
  Users,
} from "lucide-react"

import { SEO } from "@/components/SEO"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { useTypewriter } from "@/hooks/use-typewriter"

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
    },
  },
}

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
    },
  },
}

export function HomePage() {
  const typewriterText = useTypewriter([
    "beautifully managed.",
    "truly intelligent.",
    "future ready.",
    "people centric.",
  ])

  return (
    <>
      <SEO
        title="Nestor - Easy to Use HR Tech"
        description="The most user-friendly HR platform. Simplify your HR operations with our intuitive, modern solution."
      />

      {/* Hero Section */}
      <section className="relative overflow-hidden pt-10 pb-14 sm:pb-16 lg:pt-16 lg:pb-20">
        <div className="container relative">
          <motion.div
            className="mx-auto max-w-6xl"
            initial="hidden"
            animate="visible"
            variants={containerVariants}
          >
            <div className="grid grid-cols-1 items-center gap-10 lg:grid-cols-2 lg:gap-14">
              <div className="text-center lg:text-left">
                <motion.h1
                  variants={itemVariants}
                  className="text-4xl sm:text-5xl md:text-6xl font-bold tracking-tight mb-6 text-foreground"
                >
                  Your workforce,{" "}
                  <span className="bg-clip-text text-transparent bg-gradient-to-r from-blue-600 via-indigo-600 to-violet-600">
                    {typewriterText}
                    <span className="animate-pulse ml-1 text-blue-600 inline-block">|</span>
                  </span>
                </motion.h1>

                <motion.p
                  variants={itemVariants}
                  className="text-lg sm:text-xl text-muted-foreground max-w-2xl mx-auto lg:mx-0 mb-8 leading-relaxed"
                >
                  Ditch clunky spreadsheets. Switch to Nestor, the HR platform that feels like your favorite consumer app.
                  <strong> Easy to use</strong>, intuitive, and built for modern teams.
                </motion.p>

                <motion.div
                  variants={itemVariants}
                  className="flex flex-col sm:flex-row items-center lg:items-start justify-center lg:justify-start gap-3"
                >
                  <Button
                    size="lg"
                    className="h-12 px-8 text-[15px] rounded-full shadow-[0_14px_44px_rgba(37,99,235,0.22)] hover:shadow-[0_18px_56px_rgba(37,99,235,0.26)] transition-shadow"
                    asChild
                  >
                    <Link to="/demo">
                      Start Free Trial <ArrowRight className="ml-2 h-4 w-4" />
                    </Link>
                  </Button>
                  <Button
                    size="lg"
                    variant="outline"
                    className="h-12 px-8 text-[15px] rounded-full bg-background/70 hover:bg-background"
                    asChild
                  >
                    <Link to="/product">See How It Works</Link>
                  </Button>
                </motion.div>
              </div>

              {/* Illustrations Section */}
              <motion.div variants={itemVariants} className="grid grid-cols-1 md:grid-cols-3 gap-4 lg:gap-5">
                <div className="rounded-2xl bg-white/70 backdrop-blur border border-border/60 p-4 shadow-[0_18px_60px_rgba(15,23,42,0.08)] hover:shadow-[0_22px_70px_rgba(15,23,42,0.10)] transition-shadow group">
                  <div className="aspect-square rounded-xl bg-muted/50 mb-4 flex items-center justify-center overflow-hidden">
                    <img
                      src="/images/illustrations/hero-team.png"
                      alt="Friendly Team Collaboration"
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                    />
                  </div>
                  <h3 className="text-[15px] font-semibold mb-1.5">Friendly Teams</h3>
                  <p className="text-sm text-muted-foreground">
                    Collaborate effortlessly with a platform designed for humans, not just resources.
                  </p>
                </div>

                <div className="rounded-2xl bg-white/70 backdrop-blur border border-border/60 p-4 shadow-[0_18px_60px_rgba(15,23,42,0.08)] hover:shadow-[0_22px_70px_rgba(15,23,42,0.10)] transition-shadow group">
                  <div className="aspect-square rounded-xl bg-muted/50 mb-4 flex items-center justify-center overflow-hidden">
                    <img
                      src="/images/illustrations/analytics-growth.png"
                      alt="Growth Analytics"
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                    />
                  </div>
                  <h3 className="text-[15px] font-semibold mb-1.5">Growth Analytics</h3>
                  <p className="text-sm text-muted-foreground">
                    Visualize data with beautiful, easy-to-read charts that help you make better decisions.
                  </p>
                </div>

                <div className="rounded-2xl bg-white/70 backdrop-blur border border-border/60 p-4 shadow-[0_18px_60px_rgba(15,23,42,0.08)] hover:shadow-[0_22px_70px_rgba(15,23,42,0.10)] transition-shadow group">
                  <div className="aspect-square rounded-xl bg-muted/50 mb-4 flex items-center justify-center overflow-hidden">
                    <img
                      src="/images/illustrations/mobile-app.png"
                      alt="Mobile Freedom"
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                    />
                  </div>
                  <h3 className="text-[15px] font-semibold mb-1.5">Mobile Freedom</h3>
                  <p className="text-sm text-muted-foreground">
                    Empower your team to work from anywhere with a top-tier mobile experience.
                  </p>
                </div>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-16 md:py-20 relative bg-white/60">
        <div className="container">
          <div className="text-center max-w-3xl mx-auto mb-10 md:mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Complete toolkit, zero complexity</h2>
            <p className="text-muted-foreground text-lg">
              Everything you need to manage your team, stripped of the clutter.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {/* Core HR */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1, duration: 0.5 }}
              viewport={{ once: true }}
            >
              <Card className="h-full bg-white/70 backdrop-blur transition-[transform,box-shadow] border-border/60 shadow-[0_18px_60px_rgba(15,23,42,0.08)] overflow-hidden group hover:-translate-y-0.5 hover:shadow-[0_22px_70px_rgba(15,23,42,0.10)]">
                <div className="aspect-video bg-muted/40 relative overflow-hidden">
                  <img src="/images/illustrations/core-hr.png" alt="Core HR" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                </div>
                <CardHeader>
                  <CardTitle className="text-xl flex items-center gap-2">
                    <Users className="w-5 h-5 text-primary" /> Core HR
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground">Digital employee records & org charts.</p>
                </CardContent>
              </Card>
            </motion.div>

            {/* Time & Attendance */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.5 }}
              viewport={{ once: true }}
            >
              <Card className="h-full bg-white/70 backdrop-blur transition-[transform,box-shadow] border-border/60 shadow-[0_18px_60px_rgba(15,23,42,0.08)] overflow-hidden group hover:-translate-y-0.5 hover:shadow-[0_22px_70px_rgba(15,23,42,0.10)]">
                <div className="aspect-video bg-muted/40 relative overflow-hidden">
                  <img src="/images/illustrations/attendance.png" alt="Time & Attendance" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                </div>
                <CardHeader>
                  <CardTitle className="text-xl flex items-center gap-2">
                    <Clock className="w-5 h-5 text-primary" /> Time & Attendance
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground">Geo-fenced tracking & shift planning.</p>
                </CardContent>
              </Card>
            </motion.div>

            {/* Payroll */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.5 }}
              viewport={{ once: true }}
            >
              <Card className="h-full bg-white/70 backdrop-blur transition-[transform,box-shadow] border-border/60 shadow-[0_18px_60px_rgba(15,23,42,0.08)] overflow-hidden group hover:-translate-y-0.5 hover:shadow-[0_22px_70px_rgba(15,23,42,0.10)]">
                <div className="aspect-video bg-muted/40 relative overflow-hidden">
                  <img src="/images/illustrations/payroll.png" alt="Payroll" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                </div>
                <CardHeader>
                  <CardTitle className="text-xl flex items-center gap-2">
                    <Shield className="w-5 h-5 text-primary" /> Payroll
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground">Automated salary & tax compliance.</p>
                </CardContent>
              </Card>
            </motion.div>

            {/* Performance (Placeholder Style) */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4, duration: 0.5 }}
              viewport={{ once: true }}
            >
              <Card className="h-full bg-white/70 backdrop-blur transition-[transform,box-shadow] border-border/60 shadow-[0_18px_60px_rgba(15,23,42,0.08)] overflow-hidden group hover:-translate-y-0.5 hover:shadow-[0_22px_70px_rgba(15,23,42,0.10)]">
                <div className="aspect-video bg-muted/40 relative overflow-hidden flex items-center justify-center">
                  <Sparkles className="w-16 h-16 text-orange-400 opacity-20 group-hover:scale-110 transition-transform duration-500" />
                </div>
                <CardHeader>
                  <CardTitle className="text-xl flex items-center gap-2">
                    <Sparkles className="w-5 h-5 text-primary" /> Performance
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground">OKRs, 360 reviews & feedback loops.</p>
                </CardContent>
              </Card>
            </motion.div>

            {/* Analytics (Reusing Analytics Image) */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5, duration: 0.5 }}
              viewport={{ once: true }}
            >
              <Card className="h-full bg-white/70 backdrop-blur transition-[transform,box-shadow] border-border/60 shadow-[0_18px_60px_rgba(15,23,42,0.08)] overflow-hidden group hover:-translate-y-0.5 hover:shadow-[0_22px_70px_rgba(15,23,42,0.10)]">
                <div className="aspect-video bg-muted/40 relative overflow-hidden">
                  <img src="/images/illustrations/analytics-growth.png" alt="Analytics" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                </div>
                <CardHeader>
                  <CardTitle className="text-xl flex items-center gap-2">
                    <BarChart3 className="w-5 h-5 text-primary" /> Analytics
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground">Real-time workforce insights.</p>
                </CardContent>
              </Card>
            </motion.div>

            {/* Global Ops (Placeholder Style) */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6, duration: 0.5 }}
              viewport={{ once: true }}
            >
              <Card className="h-full bg-white/70 backdrop-blur transition-[transform,box-shadow] border-border/60 shadow-[0_18px_60px_rgba(15,23,42,0.08)] overflow-hidden group hover:-translate-y-0.5 hover:shadow-[0_22px_70px_rgba(15,23,42,0.10)]">
                <div className="aspect-video bg-muted/40 relative overflow-hidden flex items-center justify-center">
                  <Globe className="w-16 h-16 text-indigo-400 opacity-20 group-hover:scale-110 transition-transform duration-500" />
                </div>
                <CardHeader>
                  <CardTitle className="text-xl flex items-center gap-2">
                    <Globe className="w-5 h-5 text-primary" /> Global Ops
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground">Multi-currency & multi-country support.</p>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative overflow-hidden bg-slate-950 py-16 md:py-20">
        <div className="absolute inset-0 bg-gradient-to-b from-slate-900 to-slate-950" />
        <div className="absolute -top-24 left-1/2 h-64 w-[900px] -translate-x-1/2 rounded-full bg-primary/20 blur-[120px] opacity-60" />

        <div className="container relative text-center">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold mb-4 tracking-tight text-white">
              Ready to simplify HR?
            </h2>
            <p className="text-base md:text-lg text-slate-300 mb-8">
              Join hundreds of companies who switched to the easier way.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <Button size="lg" className="h-12 px-8 rounded-full" asChild>
                <Link to="/demo">Get Started Now</Link>
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="h-12 px-8 rounded-full border-white/25 bg-transparent text-white hover:bg-white/10 hover:text-white"
                asChild
              >
                <Link to="/contact">Talk to Sales</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>
    </>
  )
}
